import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

// ==========================================
// 1. DATA STRUCTURE FOR MENUS
// ==========================================
const REPORT_MENUS = [
  {
    category: "Clinical & Wards",
    icon: "bi-hospital",
    items: [
      { name: "OPD List", path: "/admin-reports/opd-list", icon: "bi-calendar2-check", desc: "Out-patient department records" },
      { name: "IPD List", path: "/admin-reports/ipd-list", icon: "bi-file-medical", desc: "In-patient admission list" },
      { name: "IPD Report", path: "/admin-reports/ipd-report", icon: "bi-clipboard-data", desc: "Detailed IPD analytics" }
    ]
  },
  {
    category: "Billing & Revenue",
    icon: "bi-receipt-cutoff",
    items: [
      { name: "Billing Summary", path: "/admin-reports/billing-summary", icon: "bi-graph-up-arrow", desc: "Overall billing overview" },
      { name: "Billing Payment", path: "/admin-reports/billing-payment", icon: "bi-credit-card", desc: "Payment collection details" },
      { name: "Due List", path: "/admin-reports/due-list", icon: "bi-exclamation-square", desc: "Outstanding dues report" }
    ]
  },
  {
    category: "Accounts & Finance",
    icon: "bi-wallet2",
    items: [
      { name: "Expense Report", path: "/admin-reports/expense-report", icon: "bi-cash-stack", desc: "All expense transactions" },
      { name: "Daybook Report", path: "/admin-reports/daybook", icon: "bi-book", desc: "Daily transaction journal" },
      { name: "Financial Ledger", path: "/admin-reports/financial-ledger", icon: "bi-journal-text", desc: "Complete financial ledger" }
    ]
  },
  {
    category: "Pharmacy Analytics",
    icon: "bi-capsule-pill",
    items: [
      { name: "Dashboard Report", path: "/admin-reports/pharma-dashboard", icon: "bi-speedometer2", desc: "Pharmacy KPI overview" },
      { name: "Inventory Report", path: "/admin-reports/pharma-inventory", icon: "bi-box-seam", desc: "Stock & inventory status" },
      { name: "Purchase Report", path: "/admin-reports/pharma-purchase", icon: "bi-cart-plus", desc: "Purchase transactions" },
      { name: "Sales Report", path: "/admin-reports/pharma-sales", icon: "bi-cart-check", desc: "Sales performance" },
      { name: "Expiry Report", path: "/admin-reports/pharma-expiry", icon: "bi-shield-exclamation", desc: "Expiring medicines alert" },
      { name: "Financial Report", path: "/admin-reports/pharma-financial", icon: "bi-currency-rupee", desc: "Pharmacy P&L summary" }
    ]
  }
];

// ==========================================
// 2. EXECUTIVE NAVBAR (light, single slate accent)
// ==========================================
const AdminReportNavbar = ({ mobileOpen, setMobileOpen }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setActiveMenu(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const toggleMenu = (index) => setActiveMenu(activeMenu === index ? null : index);

  const handleLinkClick = (path) => {
    setActiveMenu(null);
    setMobileOpen(false);
    navigate(path);
  };

  const isParentActive = (items) => items.some(item => location.pathname.includes(item.path));

  return (
    <div className="exec-nav-container" ref={navRef}>
      {/* Hamburger (mobile only) */}
      <button className="exec-hamburger" onClick={() => setMobileOpen(true)} title="Menu" aria-label="Open menu">
        <i className="bi bi-list"></i>
      </button>

      {/* Brand */}
      <div className="exec-brand" onClick={() => navigate('/dashboard')} title="Back to Main HMS">
        <div className="back-icon"><i className="bi bi-grid-3x3-gap-fill"></i></div>
        <div className="brand-meta">
          <span className="brand-text">HMS Dashboard</span>
          <span className="brand-sub">Exit Module</span>
        </div>
      </div>

      <div className="exec-divider"></div>

      {/* Module Identity */}
      <div className="module-identity">
        <div className="identity-icon">
          <i className="bi bi-bar-chart-line-fill"></i>
        </div>
        <div className="identity-meta">
          <div className="identity-title">Analytics Center</div>
          <div className="identity-sub">Executive Reports & MIS</div>
        </div>
      </div>

      <div className="exec-divider"></div>

      {/* Main Menus */}
      <div className="exec-menu-bar">
        {REPORT_MENUS.map((menu, idx) => {
          const isActive = isParentActive(menu.items);
          return (
            <div className="exec-menu-item" key={idx}>
              <button
                className={`exec-menu-btn ${activeMenu === idx || isActive ? 'active' : ''}`}
                onClick={() => toggleMenu(idx)}
                onMouseEnter={() => activeMenu !== null && setActiveMenu(idx)}
              >
                <i className={`bi ${menu.icon} menu-icon`}></i>
                <span>{menu.category}</span>
                <i className={`bi bi-chevron-down dropdown-chevron ${activeMenu === idx ? 'rotate' : ''}`}></i>
              </button>

              {activeMenu === idx && (
                <div className="exec-dropdown">
                  <div className="exec-dropdown-header">
                    <div className="dh-icon">
                      <i className={`bi ${menu.icon}`}></i>
                    </div>
                    <div className="dh-meta">
                      <div className="dh-title">{menu.category}</div>
                      <div className="dh-sub">{menu.items.length} reports available</div>
                    </div>
                  </div>
                  <div className="exec-dropdown-body">
                    {menu.items.map((link, lIdx) => {
                      const isLinkActive = location.pathname.includes(link.path);
                      return (
                        <div
                          className={`exec-dropdown-link ${isLinkActive ? 'active-link' : ''}`}
                          key={lIdx}
                          onClick={() => handleLinkClick(link.path)}
                        >
                          <div className="link-icon-bg">
                            <i className={`bi ${link.icon}`}></i>
                          </div>
                          <div className="link-meta">
                            <div className="link-name">{link.name}</div>
                            <div className="link-desc">{link.desc}</div>
                          </div>
                          <i className="bi bi-arrow-right link-arrow"></i>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Right side */}
      <div className="exec-nav-right">
        <div className="env-pill">
          <span className="env-dot"></span>
          <span>LIVE</span>
        </div>
        <div className="admin-badge">
          <i className="bi bi-shield-lock-fill"></i> <span>Admin Center</span>
        </div>
      </div>

      {/* ============ MOBILE DRAWER ============ */}
      {mobileOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-drawer-head">
              <div className="md-module">
                <div className="md-module-icon"><i className="bi bi-bar-chart-line-fill"></i></div>
                <div>
                  <div className="md-module-name">Analytics Center</div>
                  <div className="md-module-sub">Executive Reports</div>
                </div>
              </div>
              <button className="md-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="mobile-drawer-body">
              <button className="md-exit" onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}>
                <i className="bi bi-box-arrow-left"></i> Exit to Main Dashboard
              </button>

              {REPORT_MENUS.map((menu, idx) => (
                <div className="md-section" key={idx}>
                  <div className="md-section-title">
                    <i className={`bi ${menu.icon}`}></i> {menu.category}
                  </div>
                  {menu.items.map((link, lIdx) => {
                    const isLinkActive = location.pathname.includes(link.path);
                    return (
                      <div
                        className={`md-link ${isLinkActive ? 'md-link-active' : ''}`}
                        key={lIdx}
                        onClick={() => handleLinkClick(link.path)}
                      >
                        <i className={`bi ${link.icon}`}></i>
                        <div className="md-link-meta">
                          <span className="md-link-name">{link.name}</span>
                          <span className="md-link-desc">{link.desc}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 3. MAIN LAYOUT WRAPPER
// ==========================================
const AdminReportLayout = () => {
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Get current page name from URL
  const getCurrentPageName = () => {
    for (const menu of REPORT_MENUS) {
      for (const item of menu.items) {
        if (location.pathname.includes(item.path)) {
          return { name: item.name, category: menu.category, icon: item.icon };
        }
      }
    }
    return { name: 'Reports Home', category: 'Analytics', icon: 'bi-bar-chart-line' };
  };

  const currentPage = getCurrentPageName();

  const formatDate = (date) =>
    date.toLocaleDateString('en-IN', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    });

  const formatTime = (date) =>
    date.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });

  return (
    <div className="admin-layout-wrapper">
      <AdminReportNavbar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Sub-Header (Compact Single Row) */}
      <div className="canvas-header d-print-none">
        <div className="header-left">
          <div className="page-title-icon">
            <i className={`bi ${currentPage.icon}`}></i>
          </div>
          <div className="page-title-block">
            <h6 className="page-title">{currentPage.name}</h6>
            <div className="breadcrumb-row">
              <i className="bi bi-house-door-fill bc-home"></i>
              <i className="bi bi-chevron-right bc-sep"></i>
              <span className="bc-item">Analytics</span>
              <i className="bi bi-chevron-right bc-sep"></i>
              <span className="bc-item">{currentPage.category}</span>
              <i className="bi bi-chevron-right bc-sep"></i>
              <span className="bc-current">{currentPage.name}</span>
            </div>
          </div>
        </div>

        <div className="header-right">
          <div className="info-pill">
            <i className="bi bi-calendar3"></i>
            <span>{formatDate(currentTime)}</span>
          </div>
          <div className="info-pill time-pill">
            <i className="bi bi-clock"></i>
            <span>{formatTime(currentTime)}</span>
          </div>
          <button className="action-btn" title="Refresh Data">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button className="action-btn primary-btn" title="Export / Print" onClick={() => window.print()}>
            <i className="bi bi-printer"></i>
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Main Body */}
      <div className="admin-main-canvas">
        <div className="canvas-body">
          <div className="exec-paper">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Footer Strip */}
      <div className="canvas-footer d-print-none">
        <div className="footer-left">
          <span className="footer-item">
            <span className="footer-dot-live"></span>
            <span className="footer-online">All Systems Operational</span>
          </span>
          <span className="footer-divider"></span>
          <span className="footer-item">
            <i className="bi bi-database-check"></i>
            <span>Real-time Data</span>
          </span>
        </div>
        <div className="footer-right">
          <span className="footer-item version-tag">
            <i className="bi bi-shield-fill-check"></i>
            <span>HMS Analytics v3.0</span>
          </span>
        </div>
      </div>

      <style>{`
        /* ===========================================================
           THEME TOKENS — Light + Cyan/Sky (executive analytics)
           =========================================================== */
        .admin-layout-wrapper {
          --acc: #0891b2;        /* cyan-600 */
          --acc-dark: #0e7490;   /* cyan-700 */
          --acc-soft: #ecfeff;   /* cyan-50  */
          --acc-softer: #f8fafc; /* slate-50 (neutral canvas) */
          --acc-border: #a5f3fc; /* cyan-200 */
          --bg: #ffffff;
          --border: #e2e8f0;
          --text: #1e293b;
          --text-soft: #64748b;
          --text-muted: #94a3b8;

          display: flex;
          flex-direction: column;
          height: 100vh;
          background-color: var(--acc-softer);
          font-family: 'Inter', system-ui, sans-serif;
          overflow: hidden;
        }

        /* ============================
           NAVBAR (light)
           ============================ */
        .exec-nav-container {
          height: 58px;
          background: var(--bg);
          border-bottom: 1px solid var(--border);
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06),
                      0 1px 2px rgba(15, 23, 42, 0.04);
          display: flex;
          align-items: center;
          padding: 0 18px;
          color: var(--text);
          user-select: none;
          z-index: 1000;
          position: relative;
        }
        .exec-nav-container::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #22d3ee 0%, #0891b2 50%, #0e7490 100%);
        }

        /* Hamburger — hidden on desktop, shown on mobile */
        .exec-hamburger {
          display: none;
          width: 38px; height: 38px;
          border: 1px solid var(--border);
          background: var(--acc-softer);
          border-radius: 9px;
          color: var(--text);
          font-size: 20px;
          align-items: center; justify-content: center;
          cursor: pointer; margin-right: 10px;
          transition: all 0.2s;
        }
        .exec-hamburger:hover { background: var(--acc-soft); color: var(--acc); }

        .exec-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 6px 14px 6px 6px;
          border-radius: 9px;
          transition: all 0.2s;
          background: var(--acc-softer);
          border: 1px solid var(--border);
        }
        .exec-brand:hover {
          background: var(--acc-soft);
          border-color: var(--acc-border);
        }

        .back-icon {
          width: 30px; height: 30px;
          background: linear-gradient(135deg, #22d3ee 0%, #0891b2 100%);
          border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
          box-shadow: 0 2px 6px rgba(8, 145, 178, 0.35);
        }

        .brand-meta {
          display: flex; flex-direction: column; line-height: 1.1;
        }
        .brand-text {
          font-size: 13px; font-weight: 700;
          color: var(--text); letter-spacing: 0.2px;
        }
        .brand-sub {
          font-size: 9px; font-weight: 700;
          color: var(--text-muted); text-transform: uppercase;
          letter-spacing: 0.5px; margin-top: 1px;
        }

        .exec-divider {
          width: 1px; height: 32px;
          background: var(--border);
          margin: 0 14px;
        }

        /* Module Identity */
        .module-identity {
          display: flex; align-items: center; gap: 10px;
        }
        .identity-icon {
          width: 36px; height: 36px;
          background: var(--acc-soft);
          border: 1px solid var(--acc-border);
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          color: var(--acc); font-size: 16px;
        }
        .identity-meta {
          display: flex; flex-direction: column; line-height: 1.1;
        }
        .identity-title {
          font-size: 13px; font-weight: 800; color: var(--text);
        }
        .identity-sub {
          font-size: 9px; font-weight: 700; color: var(--text-muted);
          text-transform: uppercase; letter-spacing: 0.6px; margin-top: 2px;
        }

        /* Menu Bar */
        .exec-menu-bar {
          display: flex; align-items: center; gap: 3px; flex: 1;
        }
        .exec-menu-item { position: relative; }

        .exec-menu-btn {
          background: transparent;
          border: none;
          color: var(--text-soft);
          font-size: 13px;
          font-weight: 600;
          padding: 9px 14px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          position: relative;
        }
        .menu-icon {
          font-size: 15px;
          color: var(--text-muted);
          transition: color 0.2s;
        }
        .dropdown-chevron {
          font-size: 9px;
          opacity: 0.5;
          margin-left: 2px;
          transition: transform 0.2s;
        }
        .dropdown-chevron.rotate { transform: rotate(180deg); opacity: 1; }

        .exec-menu-btn:hover {
          background: var(--acc-softer);
          color: var(--text);
        }
        .exec-menu-btn:hover .menu-icon { color: var(--acc); }

        .exec-menu-btn.active {
          background: var(--acc-soft);
          color: var(--acc-dark);
          box-shadow: inset 0 0 0 1px var(--acc-border);
        }
        .exec-menu-btn.active .menu-icon { color: var(--acc); }
        .exec-menu-btn.active::before {
          content: '';
          position: absolute;
          bottom: -3px; left: 50%;
          transform: translateX(-50%);
          width: 60%; height: 2.5px;
          background: var(--acc);
          border-radius: 2px;
        }

        /* Premium Dropdown */
        .exec-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          left: 0;
          background: #ffffff;
          border: 1px solid var(--border);
          box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.2),
                      0 10px 20px -5px rgba(15, 23, 42, 0.08);
          border-radius: 14px;
          min-width: 340px;
          z-index: 1000;
          animation: scaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
          transform-origin: top left;
          overflow: hidden;
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95) translateY(-5px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .exec-dropdown-header {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px;
          background: linear-gradient(180deg, var(--acc-softer) 0%, var(--acc-soft) 100%);
          border-bottom: 1px solid var(--border);
          position: relative;
        }
        .exec-dropdown-header::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          background: var(--acc);
        }

        .dh-icon {
          width: 36px; height: 36px;
          background: white;
          border: 1px solid var(--border);
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px;
          color: var(--acc);
          box-shadow: 0 2px 4px rgba(15,23,42,0.04);
        }
        .dh-meta { flex: 1; }
        .dh-title {
          font-size: 13px; font-weight: 800;
          color: var(--text); letter-spacing: 0.2px;
        }
        .dh-sub {
          font-size: 11px; color: var(--text-soft);
          font-weight: 500; margin-top: 2px;
        }

        .exec-dropdown-body {
          padding: 6px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          max-height: 460px;
          overflow-y: auto;
        }
        .exec-dropdown-body::-webkit-scrollbar { width: 5px; }
        .exec-dropdown-body::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 10px;
        }

        .exec-dropdown-link {
          padding: 10px 12px;
          cursor: pointer;
          border-radius: 9px;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
        }
        .link-icon-bg {
          width: 36px; height: 36px;
          background: var(--acc-softer);
          border: 1px solid var(--acc-soft);
          color: var(--text-soft);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 15px;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .link-meta { flex: 1; min-width: 0; }
        .link-name {
          font-size: 13px; font-weight: 600;
          color: var(--text); letter-spacing: 0.1px;
        }
        .link-desc {
          font-size: 11px; color: var(--text-muted);
          font-weight: 500; margin-top: 2px;
          white-space: nowrap; overflow: hidden;
          text-overflow: ellipsis;
        }
        .link-arrow {
          font-size: 12px;
          color: #cbd5e1;
          opacity: 0;
          transform: translateX(-6px);
          transition: all 0.18s;
        }

        .exec-dropdown-link:hover {
          background: var(--acc-softer);
        }
        .exec-dropdown-link:hover .link-icon-bg {
          background: white;
          border-color: var(--acc-border);
          color: var(--acc);
          box-shadow: 0 2px 6px rgba(15,23,42,0.06);
        }
        .exec-dropdown-link:hover .link-name { color: var(--acc-dark); }
        .exec-dropdown-link:hover .link-arrow {
          opacity: 1; transform: translateX(0);
          color: var(--acc);
        }

        .exec-dropdown-link.active-link {
          background: var(--acc-soft);
        }
        .exec-dropdown-link.active-link .link-icon-bg {
          background: var(--acc);
          border-color: var(--acc);
          color: white;
        }
        .exec-dropdown-link.active-link .link-name {
          color: var(--acc-dark);
          font-weight: 700;
        }

        /* Right side */
        .exec-nav-right {
          display: flex; align-items: center; gap: 10px;
        }
        .env-pill {
          display: flex; align-items: center; gap: 5px;
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.5px;
        }
        .env-dot {
          width: 6px; height: 6px;
          background: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
          animation: live-pulse 2s ease infinite;
        }
        @keyframes live-pulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25); }
          50% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1); }
        }

        .admin-badge {
          background: var(--acc-soft);
          color: var(--acc-dark);
          border: 1px solid var(--acc-border);
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
          letter-spacing: 0.3px;
        }
        .admin-badge i { color: var(--acc); }

        /* ============================
           SUB-HEADER (Compact, light)
           ============================ */
        .canvas-header {
          padding: 6px 18px;
          background: #ffffff;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
          min-height: 44px;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
          min-width: 0;
        }
        .page-title-icon {
          width: 32px; height: 32px;
          background: var(--acc-soft);
          border: 1px solid var(--acc-border);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          color: var(--acc); font-size: 15px;
          flex-shrink: 0;
        }
        .page-title-block {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
          min-width: 0;
        }
        .page-title {
          margin: 0;
          font-size: 14px;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .breadcrumb-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 10.5px;
          margin-top: 1px;
        }
        .bc-item { color: var(--text-soft); font-weight: 500; }
        .bc-home { color: var(--acc); cursor: pointer; font-size: 10px; }
        .bc-home:hover { color: var(--acc-dark); }
        .bc-sep { color: #cbd5e1; font-size: 7px; }
        .bc-current { color: var(--acc-dark); font-weight: 700; }

        .header-right {
          display: flex; align-items: center; gap: 6px;
          flex-shrink: 0;
        }
        .info-pill {
          display: flex; align-items: center; gap: 5px;
          font-size: 11px; color: #475569; font-weight: 600;
          padding: 4px 10px;
          background: var(--acc-softer);
          border: 1px solid var(--border);
          border-radius: 6px;
        }
        .info-pill i { color: var(--text-muted); font-size: 11px; }
        .time-pill {
          font-family: 'Consolas', 'Monaco', monospace;
          color: var(--text);
        }

        .action-btn {
          background: white;
          border: 1px solid var(--border);
          color: #475569;
          padding: 5px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: all 0.18s;
          height: 28px;
        }
        .action-btn:hover {
          border-color: var(--acc-border);
          color: var(--acc-dark);
          background: var(--acc-soft);
        }
        .action-btn.primary-btn {
          background: linear-gradient(135deg, #22d3ee 0%, #0891b2 100%);
          border-color: transparent;
          color: white;
          box-shadow: 0 1px 4px rgba(8, 145, 178, 0.3);
        }
        .action-btn.primary-btn:hover {
          box-shadow: 0 3px 8px rgba(8, 145, 178, 0.4);
          transform: translateY(-1px);
          color: white;
        }

        /* ============================
           CANVAS
           ============================ */
        .admin-main-canvas {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .canvas-body {
          flex: 1;
          overflow-y: auto;
          padding: 10px 14px;
        }
        .exec-paper {
          background: #ffffff;
          border-radius: 10px;
          min-height: 100%;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04),
                      0 4px 12px rgba(15, 23, 42, 0.03);
          border: 1px solid var(--border);
          padding: 14px;
          position: relative;
        }
        .canvas-body::-webkit-scrollbar { width: 8px; }
        .canvas-body::-webkit-scrollbar-track { background: transparent; }
        .canvas-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
          border: 2px solid var(--acc-softer);
        }
        .canvas-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        /* ============================
           FOOTER (light)
           ============================ */
        .canvas-footer {
          height: 30px;
          background: #ffffff;
          color: #475569;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 18px;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          border-top: 1px solid var(--border);
          box-shadow: 0 -1px 3px rgba(15, 23, 42, 0.04);
        }
        .footer-left, .footer-right {
          display: flex; align-items: center; gap: 12px;
        }
        .footer-item {
          display: flex; align-items: center; gap: 6px;
        }
        .footer-item i { font-size: 11px; color: var(--text-muted); }
        .footer-online { color: #059669; font-weight: 700; }
        .footer-dot-live {
          width: 7px; height: 7px;
          background: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
          animation: live-pulse 2s ease infinite;
        }
        .footer-divider {
          width: 1px; height: 14px;
          background: var(--border);
        }
        .version-tag {
          color: var(--acc-dark);
          font-weight: 700;
        }
        .version-tag i { color: var(--acc) !important; }

        /* ============================
           MOBILE DRAWER
           ============================ */
        .mobile-drawer-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 2000;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .mobile-drawer {
          position: absolute; top: 0; left: 0; bottom: 0;
          width: 86%; max-width: 340px;
          background: #ffffff;
          box-shadow: 4px 0 30px rgba(15, 23, 42, 0.25);
          display: flex; flex-direction: column;
          animation: drawerIn 0.25s ease;
        }
        @keyframes drawerIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .mobile-drawer-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px; border-bottom: 1px solid var(--border);
          background: linear-gradient(180deg, var(--acc-soft) 0%, #ffffff 100%);
        }
        .md-module { display: flex; align-items: center; gap: 10px; }
        .md-module-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: linear-gradient(135deg, #22d3ee 0%, #0891b2 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 18px; box-shadow: 0 2px 6px rgba(8,145,178,0.35);
        }
        .md-module-name { font-size: 14px; font-weight: 800; color: var(--text); }
        .md-module-sub {
          font-size: 9px; font-weight: 700; color: var(--text-muted);
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .md-close {
          width: 34px; height: 34px; border-radius: 8px;
          border: 1px solid var(--border); background: #fff;
          color: var(--text-soft); font-size: 15px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .md-close:hover { background: #f1f5f9; }
        .mobile-drawer-body { flex: 1; overflow-y: auto; padding: 12px; }
        .md-exit {
          width: 100%; display: flex; align-items: center; gap: 10px;
          padding: 11px 12px; margin-bottom: 14px;
          background: var(--acc-soft); color: var(--acc-dark);
          border: 1px solid var(--acc-border); border-radius: 9px;
          font-size: 13px; font-weight: 700; cursor: pointer;
        }
        .md-section { margin-bottom: 16px; }
        .md-section-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 10px; font-weight: 800; color: var(--acc-dark);
          text-transform: uppercase; letter-spacing: 0.6px;
          padding: 6px 8px; margin-bottom: 4px;
        }
        .md-link {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 12px; border-radius: 9px;
          cursor: pointer; transition: all 0.15s;
        }
        .md-link i { font-size: 16px; color: var(--text-muted); width: 18px; text-align: center; }
        .md-link-meta { display: flex; flex-direction: column; line-height: 1.25; }
        .md-link-name { font-size: 13.5px; font-weight: 600; color: #334155; }
        .md-link-desc { font-size: 10.5px; color: var(--text-muted); }
        .md-link:active, .md-link:hover {
          background: var(--acc-softer);
        }
        .md-link:hover i { color: var(--acc); }
        .md-link:hover .md-link-name { color: var(--acc-dark); }
        .md-link-active {
          background: var(--acc-soft);
        }
        .md-link-active i { color: var(--acc); }
        .md-link-active .md-link-name { color: var(--acc-dark); font-weight: 700; }

        /* ============================
           RESPONSIVE
           ============================ */
        @media (max-width: 1280px) {
          .module-identity { display: none; }
          .exec-divider:nth-of-type(2) { display: none; }
          .brand-sub { display: none; }
        }

        @media (max-width: 1100px) {
          .exec-menu-btn span { display: none; }
          .exec-menu-btn { padding: 9px 11px; }
          .info-pill span { display: none; }
          .action-btn span { display: none; }
        }

        @media (max-width: 900px) {
          .env-pill { display: none; }
          .admin-badge span { display: none; }
          .admin-badge { padding: 6px 9px; }
        }

        /* PHONE: hamburger replaces inline menu bar */
        @media (max-width: 768px) {
          .exec-hamburger { display: flex; }
          .exec-menu-bar { display: none; }
          .exec-divider { display: none; }
          .module-identity { display: none; }
          .exec-nav-right { margin-left: auto; }
          .exec-nav-container { padding: 0 12px; }
          .canvas-header { flex-direction: column; align-items: stretch; gap: 8px; }
          .header-right { flex-wrap: wrap; }
          .info-pill span { display: inline; }
          .action-btn span { display: inline; }
        }
        @media (max-width: 420px) {
          .brand-meta { display: none; }
        }

        @media print {
          .d-print-none { display: none !important; }
          .admin-layout-wrapper {
            background: white; height: auto; overflow: visible;
          }
          .admin-main-canvas { overflow: visible; }
          .canvas-body {
            padding: 0; overflow: visible;
          }
          .exec-paper {
            border: none; box-shadow: none; padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminReportLayout;